import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown } from "lucide-react";
import { useRef } from "react";
import { Icon } from "../icons.js";
/**
 * The box is a `<label>` around the select, so a click on the box focuses the select; the ×
 * inside is interactive content, which a label does not forward. Clearing puts focus back on
 * the select: the × unmounts with the value it cleared, and focus would otherwise fall to
 * `BODY`. It carries `data-select`
 * (and `data-select-set` while a value is chosen):
 * `interactions.css` fills it on hover through `--select-bg` and draws the focus ring on it
 * while the select inside has focus, since the inner control keeps `outline: none` inline. The
 * × is a sibling button with its own ring, so the box does not ring for it too. Disabled (a
 * failed library read, issue 197) it reads ink-2 with the default cursor and no hover fill.
 */
export function Select({ label, value, options = [], onChange, onClear, clearLabel = "Clear", style, ...rest }) {
    const set = onClear !== undefined;
    const select = useRef(null);
    return (_jsxs("label", { "data-select": "", "data-select-set": set ? "" : undefined, style: {
            display: "inline-flex", alignItems: "center", gap: "var(--space-075)",
            height: 36, padding: "0 var(--space-15)", borderRadius: "var(--radius-control)",
            background: `var(--select-bg, ${set ? "var(--accent-pale)" : "transparent"})`,
            color: set ? "var(--select-ink, var(--accent))" : rest.disabled ? "var(--ink-2)" : "var(--ink-1)",
            fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)",
            fontWeight: set ? 500 : undefined,
            cursor: rest.disabled ? "default" : "pointer", transition: "background-color var(--dur-fast) var(--ease-out)",
            ...style,
        }, children: [label && _jsx("span", { style: { color: "var(--ink-2)" }, children: label }), _jsx("select", { ref: select, value: value, onChange: onChange, style: {
                    appearance: "none", background: "transparent", border: "none", outline: "none",
                    color: "inherit", font: "inherit", cursor: "inherit", padding: 0,
                    // As wide as the chosen value, not the longest option (E sizes to its label).
                    fieldSizing: "content",
                }, ...rest, children: options.map((o) => {
                    const optionValue = typeof o === "string" ? o : o.value;
                    const optionLabel = typeof o === "string" ? o : o.label;
                    return (_jsx("option", { value: optionValue, children: optionLabel }, optionValue));
                }) }), set ? (_jsx("button", { type: "button", "data-select-clear": "", "aria-label": clearLabel, onClick: () => {
                    onClear?.();
                    select.current?.focus();
                }, style: {
                    display: "inline-grid", placeItems: "center", flex: "none", padding: 0,
                    width: 20, height: 20, fontSize: 22, fontWeight: 300, lineHeight: 1, fontFamily: "inherit",
                    margin: "0 calc(var(--space-05) * -1) 0 0", border: "none", background: "transparent",
                    color: "inherit", cursor: "pointer", borderRadius: "var(--radius-inline)",
                }, children: "\u00D7" })) : (_jsx("span", { "aria-hidden": true, style: { display: "inline-flex", color: "var(--ink-2)", pointerEvents: "none" }, children: _jsx(Icon, { icon: ChevronDown, size: 16 }) }))] }));
}
